import React, { useState, useEffect } from 'react';
import axios from 'axios';

const DiscourseAnalysisReport = ({ transcript, patientId, doctorId, durationSeconds, clinicalContext, empathyScore, consultationData }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (transcript && transcript.length > 10 && consultationData && consultationData.session_id) {
      console.log('DiscourseAnalysisReport: Transcript received:', transcript.substring(0, 100));
      fetchAnalysis();
    }
  }, [transcript, consultationData]);

  

  console.log("analysis", analysis);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const API_BASE_URL = process.env.NODE_ENV === "production"
        ? (process.env.REACT_APP_PROD_API_BASE_URL || "https://test.conversationaily.ai")
        : (process.env.REACT_APP_DEV_API_BASE_URL || "https://testone.conversationaily.ai");
        
      const response = await axios.post(`${API_BASE_URL}/api/user/discourse/analyze`, {
        transcript,
        consultation_session_id: consultationData.session_id,
        // patient_id: patientId,
        // doctor_id: doctorId,
        duration_seconds: durationSeconds,
        clinical_context: clinicalContext,
        empathy_score: empathyScore
      });
      setAnalysis(response.data);
    } catch (error) {
      console.error('Discourse analysis failed, using mock data:', error);
      console.log('Empathy score received:', empathyScore);
      // Fallback to mock data for demonstration
      setAnalysis({
        doctor_id: doctorId || "0012",
        consult_id: patientId,
        consult_type: "One-way consultation",
        clinical_context: clinicalContext || "General consultation",
        language: "Hindi-English (code-mixed)",
        duration: `~${Math.floor(durationSeconds/60)} minutes`,
        empathy_score: empathyScore !== undefined ? empathyScore : 5,
        communication_mode: "Directive/Clinical with explanatory metaphors",
        analysis: {
          overall_style: {
            observed: "Directive–clinical communication",
            why_matters: "Clear and efficient, but can feel one-sided",
            reflection: "Where could collaboration be signaled once?"
          },
          empathy_signal: {
            observed: "Low–moderate (4/10)",
            why_matters: "Emotional cues were limited despite clear advice",
            reflection: "What emotion could have been named or normalized?"
          },
          tone_balance: {
            observed: "Clinical > Reassuring",
            why_matters: "Risk explanations outweighed reassurance",
            reflection: "Could one reassuring line balance risk talk?"
          },
          voice_stance: {
            observed: "Authoritative, teacher-like",
            why_matters: "Patient agency not strongly signaled",
            reflection: "Where could 'hum saath milkar' fit naturally?"
          },
          what_came_through_well: {
            observed: "Clarity, respect, practical support",
            why_matters: "Builds trust and understanding",
            reflection: "Keep these strengths"
          },
          strengths: {
            observed: "Clarity, respect, practical support",
            why_matters: "Builds trust and understanding",
            reflection: "Keep these strengths"
          },
          under_signaled: {
            observed: "Normalization, partnership",
            why_matters: "Patients may feel pressured or fearful",
            reflection: "What single phrase could soften this?"
          }
        },
        critical_insight: "Complication-focused framing was repeated, while normalizing language was absent",
        example_phrase: "Insulin lena padega",
        suggested_alternative: "Is stage par insulin shuru karna aam taur par madad karta hai",
        small_shift: "Yeh dar lagna bahut aam baat hai — hum isko aapke routine ke hisaab se manage kar sakte hain."
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 rounded-lg shadow-md p-6 mt-6 max-w-4xl mx-auto border">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">📊 On-Screen Discourse Analysis Report</h3>
        <div className="animate-pulse flex items-center">
          <div className="w-4 h-4 bg-blue-500 rounded-full mr-2 animate-bounce"></div>
          <span className="text-gray-800">Analyzing consultation discourse...</span>
        </div>
      </div>
    );
  }

  if (!analysis && !loading) {
    return (
      <div className="bg-gray-50 rounded-lg shadow-md p-6 mt-6 max-w-4xl mx-auto border">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">📊 On-Screen Discourse Analysis Report</h3>
        <div className="text-gray-600">No analysis available. Please record a consultation first.</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg shadow-md p-6 mt-6 max-w-4xl mx-auto border">
      <div className="border-b border-gray-300 pb-4 mb-6 text-center">
        <h2 className="text-xl font-bold text-blue-800">ConversationAIly™</h2>
        <h3 className="text-lg font-semibold text-gray-800">One-Way Clinical Discourse Analysis Report</h3>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6 text-sm text-gray-800">
        <div><strong className="text-gray-900">Doctor ID:</strong> {analysis.doctor_id}</div>
        <div><strong className="text-gray-900">Consult ID:</strong> {analysis.consult_id}</div>
        <div><strong className="text-gray-900">Consult Type:</strong> {analysis.consult_type}</div>
        <div><strong className="text-gray-900">Clinical Context:</strong> {analysis.clinical_context}</div>
        <div><strong className="text-gray-900">Language:</strong> {analysis.language}</div>
        <div><strong className="text-gray-900">Duration:</strong> {analysis.duration}</div>
        <div><strong className="text-gray-900">Overall Empathy Score:</strong> 
          <span className={`ml-2 px-2 py-1 rounded ${(empathyScore ?? analysis.empathy_score) >= 7 ? 'bg-green-100 text-green-800' : 
            (empathyScore ?? analysis.empathy_score) >= 4 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
            {empathyScore ?? analysis.empathy_score} / 10
          </span>
        </div>
        <div><strong className="text-gray-900">Dominant Communication Mode:</strong> <span className="text-gray-800">{analysis.communication_mode}</span></div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-400 text-sm bg-white">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-400 p-3 text-left font-semibold text-gray-900">Reflection Area</th>
              <th className="border border-gray-400 p-3 text-left font-semibold text-gray-900">What the AI Observed</th>
              <th className="border border-gray-400 p-3 text-left font-semibold text-gray-900">Why It Matters</th>
              <th className="border border-gray-400 p-3 text-left font-semibold text-gray-900">Quick Reflection / Alternative</th>
            </tr>
          </thead>
          <tbody>
            {analysis.analysis && Object.entries(analysis.analysis).map(([key, value]) => (
              <tr key={key} className="hover:bg-gray-50">
                <td className="border border-gray-400 p-3 font-medium capitalize text-gray-900">
                  {key == 'what_came_through_well' ? 'What Came Through Well' : key.replace('_', ' ')}
                </td>
                <td className="border border-gray-400 p-3 text-gray-800">{value.observed}</td>
                <td className="border border-gray-400 p-3 text-gray-800">{value.why_matters}</td>
                <td className="border border-gray-400 p-3 text-blue-700">{value.reflection}</td>
              </tr>
            ))}
            {analysis.critical_insight && (
              <tr className="hover:bg-gray-50">
                <td className="border border-gray-400 p-3 font-medium text-gray-900">Evidence-Anchored Insight (Critical)</td>
                <td className="border border-gray-400 p-3 text-gray-800">{analysis.critical_insight}</td>
                <td className="border border-gray-400 p-3 text-gray-800">This combination commonly heightens fear and reduces felt support</td>
                <td className="border border-gray-400 p-3 text-blue-700">Where could one normalizing sentence be inserted?</td>
              </tr>
            )}
            {analysis.example_phrase && (
              <tr className="hover:bg-gray-50">
                <td className="border border-gray-400 p-3 font-medium text-gray-900">Example Phrase Detected</td>
                <td className="border border-gray-400 p-3 text-gray-800 italic">"{analysis.example_phrase}"</td>
                <td className="border border-gray-400 p-3 text-gray-800">High-obligation wording increases pressure</td>
                <td className="border border-gray-400 p-3 text-blue-700">Does this match your intent?</td>
              </tr>
            )}
            {analysis.suggested_alternative && (
              <tr className="hover:bg-gray-50">
                <td className="border border-gray-400 p-3 font-medium text-gray-900">Suggested Alternative (Optional)</td>
                <td className="border border-gray-400 p-3 text-gray-800 italic">"{analysis.suggested_alternative}"</td>
                <td className="border border-gray-400 p-3 text-gray-800">Retains clinical authority while reducing perceived coercion</td>
                <td className="border border-gray-400 p-3 text-blue-700">Would this fit your speaking style?</td>
              </tr>
            )}
            {analysis.small_shift && (
              <tr className="hover:bg-gray-50">
                <td className="border border-gray-400 p-3 font-medium text-gray-900">Try One Small Shift (Optional)</td>
                <td className="border border-gray-400 p-3 text-gray-800 italic">"{analysis.small_shift}"</td>
                <td className="border border-gray-400 p-3 text-gray-800">Improves empathy without adding time</td>
                <td className="border border-gray-400 p-3 text-blue-700">Try once in a similar consult</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DiscourseAnalysisReport;